import React, { useState } from 'react';

import {
  Box,
  Tab,
  Tabs,
  Button,
  Typography,
  TextField,
  CircularProgress,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  MenuBook as PlaybookIcon,
  SmartToy as AutomationIcon,
  AutoFixHigh as EnrichmentIcon,
  Extension as SkillIcon,
  AutoAwesome as LearnedIcon,
  Description as SystemIcon,
  Article as ReportIcon,
  Publish as PublishIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import FormActionBar from '../../../shared/components/FormActionBar';
import TagEditor from '../../../shared/components/TagEditor';
import type { PromptTypeType } from '../../../types/backend';
import { SKILL_APPLICABILITY_LABELS } from '../../../shared/constants/promptTypes';
import { usePromptForm } from '../hooks/usePromptForm';
import { formatTimestamp } from '../utils/promptUtils';
import PromptContentEditor from './PromptContentEditor';
import PromptSettings from './PromptSettings';

interface EditPromptPanelProps {
  form: ReturnType<typeof usePromptForm>;
  /**
   * Fields of the thing this prompt belongs to, shown above it.
   *
   * An agent is edited here because its shell is the prompt: its own settings sit on top of the
   * body, in the same pane, because selecting the agent is the only navigation there should be.
   */
  settings?: React.ReactNode;
  /**
   * The owner's own row, saved by the pane's one Save alongside the prompt.
   *
   * Two rows are written, so two things can be dirty, but the pane is one thing to the person
   * editing it and a second Save button beside the first is how it stops reading as one.
   */
  alsoSave?: { dirty: boolean; saving: boolean; save: () => Promise<boolean> };
  /**
   * A second destination for the pane, beside the prompt itself.
   *
   * Only an agent has one: its toolbox is as tall as its prompt, so the two share a tab row
   * rather than a scrollbar. With no second destination there is no row, because a tab bar
   * of one is furniture.
   */
  extraTab?: { label: string; content: React.ReactNode };
}

type KindMeta = { icon: SvgIconComponent; word: string; color: 'primary' | 'secondary' | 'info' | 'success' };

const KIND: Record<PromptTypeType, KindMeta> = {
  playbook: { icon: PlaybookIcon, word: 'PLAYBOOK', color: 'primary' },
  system_prompt: { icon: SystemIcon, word: 'SYSTEM', color: 'primary' },
  automation_prompt: { icon: AutomationIcon, word: 'AUTOMATION', color: 'secondary' },
  enrichment: { icon: EnrichmentIcon, word: 'ENRICHMENT', color: 'info' },
  report: { icon: ReportIcon, word: 'REPORT', color: 'primary' },
  skill: { icon: SkillIcon, word: 'SKILL', color: 'success' },
  learned_skill: { icon: LearnedIcon, word: 'LEARNED SKILL', color: 'success' },
};

const SKILL_TYPES: PromptTypeType[] = ['skill', 'learned_skill'];

const EditPromptPanel: React.FC<EditPromptPanelProps> = ({ form, settings, alsoSave, extraTab }) => {
  const [tab, setTab] = useState<'prompt' | 'extra'>('prompt');
  const saveAll = async () => {
    // Two rows behind one button, so a refusal in the first stops the second: writing the
    // prompt after the spec was rejected half-applies the change and shows the error and
    // the success message together.
    if (alsoSave?.dirty && !(await alsoSave.save())) return;
    if (form.hasChanges()) await form.handleSave();
  };

  const type = form.formData.type as PromptTypeType;
  const isSkill = SKILL_TYPES.includes(type);
  const isLearned = type === 'learned_skill';

  if (!form.selectedPrompt && !form.isNewPrompt) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {settings}
        <EmptyStateMessage
          icon={PlaybookIcon}
          title="Nothing selected"
          description="Pick an instruction or skill from the list, or create a new one"
        />
      </Box>
    );
  }

  const meta = KIND[type] ?? KIND.playbook;
  const KindIcon = meta.icon;
  const agentKind = isSkill
    ? SKILL_APPLICABILITY_LABELS[form.formData.agentType]
    : SKILL_APPLICABILITY_LABELS[type === 'automation_prompt' ? 'batch' : 'interactive'];

  const publishedVersions = (form.selectedPrompt?.availableVersions || []).filter((v) => v !== 'DRAFT');
  const isPublished = publishedVersions.length > 0;
  const liveState = isSkill
    ? (isPublished ? `v${form.selectedPrompt!.promptVersion} · in pool` : 'DRAFT · not in pool')
    : (isPublished ? `v${form.selectedPrompt!.promptVersion} · live` : 'Unpublished');
  const liveColor = isPublished ? 'success.main' : 'warning.main';

  const editable = !isLearned && !form.isViewingHistory;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Compact header: identity + version, then title · context · tags in one block */}
      <Box sx={{ px: 3, pt: 1.25, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <KindIcon sx={{ fontSize: 18 }} color={meta.color} />
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.5, lineHeight: 1 }} color={`${meta.color}.main`}>
            {meta.word}
          </Typography>
          <Typography variant="caption" color="text.secondary">· {form.formData.owner === 'global' ? 'Global' : 'Customer'} · {agentKind}</Typography>
          <Typography variant="caption" sx={{ color: liveColor, fontWeight: 600 }}>· {liveState}</Typography>
          {!form.isNewPrompt && form.selectedPrompt && (
            <Tooltip
              title={
                <>
                  Created {formatTimestamp(form.selectedPrompt.createdAt)}
                  {form.selectedPrompt.createdBy ? ` by ${form.selectedPrompt.createdBy}` : ''}
                  <br />
                  Updated {formatTimestamp(form.selectedPrompt.updatedAt)}
                </>
              }
            >
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>
                · updated {formatTimestamp(form.selectedPrompt.updatedAt)}
              </Typography>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {!form.isNewPrompt && form.selectedPrompt && isPublished && (
            <TextField
              select
              size="small"
              value={form.viewingVersion || 'DRAFT'}
              onChange={(e) => form.handleVersionChange(e.target.value)}
              disabled={form.versionLoading}
              sx={{ minWidth: 130 }}
              SelectProps={{ sx: { fontSize: '0.8rem', height: 28 } }}
            >
              <MenuItem value="DRAFT">Draft (editing)</MenuItem>
              {publishedVersions.map((v) => (
                <MenuItem key={v} value={v}>v{v}{v === form.selectedPrompt!.promptVersion ? ' (live)' : ''}</MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.25 }}>
          <TextField
            value={form.formData.title}
            onChange={(e) => form.handleChange('title', e.target.value)}
            placeholder={isSkill ? 'Skill title…' : 'Instruction title…'}
            variant="standard"
            disabled={!editable}
            InputProps={{ disableUnderline: true, sx: { fontSize: '1.2rem', fontWeight: 600 } }}
            sx={{ flexShrink: 0, maxWidth: '55%' }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {isLearned
              ? 'Synthesised — read-only.'
              : isSkill
                ? <>Agent picks this when its trigger matches · Publish to make it available to {agentKind.toLowerCase()}</>
                : 'Drives an agent run · wired to events or chat elsewhere'}
          </Typography>
        </Box>

        <Box sx={{ mt: 0.5 }}>
          <TagEditor
            value={form.formData.tags}
            onChange={(tags) => form.handleChange('tags', tags)}
            suggestions={form.allTags}
          />
        </Box>
      </Box>

      {extraTab && (
        <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{ minHeight: 36, '& .MuiTab-root': { textTransform: 'none' } }}
          >
            <Tab value="prompt" label="Prompt" sx={{ minHeight: 36, py: 0 }} />
            <Tab value="extra" label={extraTab.label} sx={{ minHeight: 36, py: 0 }} />
          </Tabs>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {settings}

        {extraTab && tab === 'extra' && extraTab.content}

        <Box sx={{ display: tab === 'prompt' ? 'flex' : 'none', flexDirection: 'column' }}>
          {isSkill && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                When to use this <Typography component="span" variant="caption" color="text.secondary">— the trigger the agent reads to decide</Typography>
              </Typography>
              <TextField
                value={form.formData.description}
                onChange={(e) => form.handleChange('description', e.target.value)}
                placeholder="e.g. Use when an RDS/Aurora failover event fires — decide whether to promote a replica and how to guard rollback."
                multiline
                minRows={2}
                fullWidth
                disabled={!editable}
                error={!!form.fieldErrors.description}
                helperText={form.fieldErrors.description || `${form.formData.description.length} / 100 min — this is what makes the agent pick the skill`}
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          <PromptSettings
            formData={form.formData}
            isNewPrompt={form.isNewPrompt}
            fieldErrors={form.fieldErrors}
            onChange={form.handleChange}
            hideDescription
          />

          {isSkill && (
            <Typography variant="subtitle2" sx={{ px: 3, pt: 2 }}>
              Steps <Typography component="span" variant="caption" color="text.secondary">— what to do once pulled in</Typography>
            </Typography>
          )}

          <PromptContentEditor
            content={form.formData.content}
            onChange={(value) => form.handleChange('content', value)}
            contentError={form.fieldErrors.content}
            viewingVersion={form.viewingVersion}
            viewingContent={form.viewingContent}
            versionLoading={form.versionLoading}
            promptType={form.formData.type}
            description={{
              text: form.formData.description,
              field: isSkill ? undefined : (
                <TextField
                  value={form.formData.description}
                  onChange={(e) => form.handleChange('description', e.target.value)}
                  error={!!form.fieldErrors.description}
                  helperText={form.fieldErrors.description || undefined}
                  label="Description"
                  placeholder="What this is for. An agent reads it to decide whether to use this."
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={!editable}
                />
              ),
            }}
          />
        </Box>

      </Box>

      {isLearned ? (
        <Box sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LockIcon fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.secondary">
            Learned skills are synthesised automatically and are read-only.
          </Typography>
        </Box>
      ) : (
        <FormActionBar
          onSave={saveAll}
          saving={form.saving || !!alsoSave?.saving}
          saveDisabled={form.publishing || (!form.hasChanges() && !alsoSave?.dirty)}
          saveLabel={form.isNewPrompt ? 'Create' : 'Save'}
          savingLabel={form.isNewPrompt ? 'Creating...' : 'Saving...'}
          size="small"
          onDelete={!form.isNewPrompt && form.selectedPrompt && !form.isViewingHistory ? form.handleDeleteClick : undefined}
          deleteDisabled={form.saving || form.publishing}
          hideSave={form.isViewingHistory}
          sx={{ px: 3, py: 1.5, mt: 0, pt: 0 }}
        >
          {form.successMessage && (
            <Typography variant="caption" color="success.main">{form.successMessage}</Typography>
          )}
          {!form.successMessage && !form.isNewPrompt && form.selectedPrompt && !form.isViewingHistory && form.hasChanges() && (
            <Typography variant="caption" color="warning.main">Unsaved changes</Typography>
          )}
          {!form.isNewPrompt && form.selectedPrompt && !form.isViewingHistory && (
            <Button
              size="small"
              variant="outlined"
              startIcon={form.publishing ? <CircularProgress size={14} /> : <PublishIcon />}
              onClick={form.handlePublish}
              disabled={form.saving || form.publishing || !form.isFormValid() || form.hasChanges() || form.justPublished}
            >
              {form.publishing ? 'Publishing...' : isSkill ? 'Publish → add to pool' : 'Publish'}
            </Button>
          )}
        </FormActionBar>
      )}
    </Box>
  );
};

export default EditPromptPanel;
